import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText, IndianRupee } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { QuotationComposer } from './QuotationComposer';

const inr = (n) => `\u20b9${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return iso;
  }
};

export const QuotationsTab = ({ authAxios }) => {
  const [quotations, setQuotations] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [qRes, lRes] = await Promise.all([
        authAxios.get('/admin/quotations'),
        authAxios.get('/admin/leads', { params: { limit: 100 } }),
      ]);
      setQuotations(qRes.data.quotations || []);
      setLeads((lRes.data.leads || []).filter((l) => l.email));
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Failed to load quotations');
      setQuotations([]);
    }
  }, [authAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedLead = useMemo(() => leads.find((l) => l.id === selectedLeadId), [leads, selectedLeadId]);

  const totalValue = useMemo(() => (quotations || []).reduce((s, q) => s + (q.total || 0), 0), [quotations]);

  const filtered = useMemo(() => {
    if (!quotations) return [];
    if (!search.trim()) return quotations;
    const s = search.toLowerCase();
    return quotations.filter(
      (q) =>
        (q.quote_number || '').toLowerCase().includes(s) ||
        (q.lead_name || '').toLowerCase().includes(s) ||
        (q.lead_email || '').toLowerCase().includes(s)
    );
  }, [quotations, search]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <div className="bg-card border border-border rounded-xl p-5" data-testid="quotations-stat-count">
          <p className="text-xs text-muted-foreground font-mono-tech tracking-wide uppercase">Quotations Sent</p>
          <p className="font-display font-semibold text-3xl mt-2">{quotations ? quotations.length : '\u2014'}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-5" data-testid="quotations-stat-value">
          <p className="text-xs text-muted-foreground font-mono-tech tracking-wide uppercase">Total Quoted Value</p>
          <p className="font-display font-semibold text-2xl mt-2 flex items-center gap-1">
            <IndianRupee className="w-5 h-5 text-[#155E6D]" />
            {(totalValue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </p>
        </div>
      </div>

      {/* Create new quotation */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2">
          <FileText className="w-4.5 h-4.5 text-[#155E6D]" size={18} /> Create New Quotation
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Pick a lead (only leads that shared an email are listed), build the itemised quotation with GST, and send it as a
          branded email. The lead's status automatically moves to QUOTATION.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
          <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
            <SelectTrigger className="h-11 sm:w-96" data-testid="quotations-lead-select">
              <SelectValue placeholder={'Select a lead\u2026'} />
            </SelectTrigger>
            <SelectContent>
              {leads.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No leads with email addresses yet.</div>
              )}
              {leads.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {`${l.name} \u00b7 ${l.project_type} \u00b7 ${l.email}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLead && (
            <QuotationComposer
              key={selectedLead.id}
              authAxios={authAxios}
              lead={selectedLead}
              onSent={() => {
                setSelectedLeadId('');
                load();
              }}
            />
          )}
        </div>
      </div>

      {/* History */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="font-display font-semibold text-lg">Quotation History</h3>
          <Input
            placeholder={'Search number, name, email\u2026'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 sm:w-72"
            data-testid="quotations-search-input"
          />
        </div>
        {(() => {
          if (quotations === null) {
            return (
              <div className="p-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            );
          }
          if (filtered.length === 0) {
            return (
              <p className="mt-5 text-sm text-muted-foreground" data-testid="quotations-empty-state">
                {search ? 'No quotations match your search.' : 'No quotations sent yet. Create your first one above.'}
              </p>
            );
          }
          return (
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Valid Until</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((q) => (
                  <TableRow key={q.id} data-testid={`quotation-row-${q.quote_number}`}>
                    <TableCell className="font-mono-tech text-xs">{q.quote_number}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(q.created_at)}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{q.lead_name}</p>
                      <p className="text-xs text-muted-foreground">{q.lead_email}</p>
                    </TableCell>
                    <TableCell className="text-sm">{(q.items || []).length}</TableCell>
                    <TableCell className="text-sm text-right whitespace-nowrap">{inr(q.gst_amount)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold whitespace-nowrap text-[#155E6D]">{inr(q.total)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{q.valid_until_str}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          );
        })()}
      </div>
    </div>
  );
};
